// Navbar layout data — follows the same pattern as heroLayouts.constants.js.
// Extracted from the 3 real navbar files (Step 8):
//   meelz-client-v2/src/layouts/headers/navbar.tsx
//   ck-client-e4f/components/header.tsx
//   ck-client-ck/components/header.tsx
// `ctaText` is authored as {en: "..."} from the start — translatable per
// Step 3, never a plain string that gets converted later.
import { SOCIAL_PLATFORM_OPTIONS } from "./socialPlatforms.constants";

export const NAVBAR_CTA_BORDER_RADIUS_OPTIONS = [
    { value: "pill",    label: "Pill" },
    { value: "rounded", label: "Rounded" },
    { value: "square",  label: "Square" },
];

export const NAVBAR_LOGO_ALIGNMENT_OPTIONS = [
    { value: "left",   label: "Left" },
    { value: "center", label: "Center" },
];

export const NAVBAR_BAR_JUSTIFY_OPTIONS = [
    { value: "start",         label: "Start (clustered left)" },
    { value: "space-between", label: "Space between (logo left, rest right)" },
    { value: "center",        label: "Center (whole bar centered)" },
];

export const NAVBAR_LINKS_ALIGNMENT_OPTIONS = [
    { value: "left",          label: "Left" },
    { value: "center",        label: "Center" },
    { value: "right",         label: "Right" },
    { value: "space-between", label: "Space between" },
];

// Alignment of the top bar's promo message — real navbar.tsx renders it
// right-aligned on desktop (`lg:text-right` inside a `lg:w-3/5` column,
// line 118-122), left/full-width on mobile. That default was previously
// hardcoded with no admin control at all (confirmed bug: the admin builder
// preview rendered the message flush-left by default, visually mismatched
// from the real live site, and publishing could never change it either way).
export const NAVBAR_TOP_BAR_MESSAGE_ALIGNMENT_OPTIONS = [
    { value: "left",   label: "Left" },
    { value: "center", label: "Center" },
    { value: "right",  label: "Right" },
];

// Order of the auth-state cluster (Login/Account button, language switcher,
// Logout button) on the right side of the bar — previously hardcoded to one
// fixed JSX order with no admin control at all (confirmed bug: publishing
// any navbar layout change never moved these 3 elements). Every brand's
// real live header uses "auth-lang-logout" (Login/Account button, then
// language switcher, then Logout) — see navbar.tsx/header.tsx desktop nav —
// so that's every layout's default; the other 2 options exist purely so an
// admin can deliberately choose a different order per brand if needed.
export const NAVBAR_AUTH_ORDER_OPTIONS = [
    { value: "auth-lang-logout", label: "Login/Account → Language → Logout" },
    { value: "lang-auth-logout", label: "Language → Login/Account → Logout" },
    { value: "lang-logout-auth", label: "Language → Logout → Login/Account" },
];

// Fixed set of stable asset IDs (not raw paths) — same pattern as hero's
// HERO_IMAGE_OPTIONS/HERO_IMAGE_ADMIN_PREVIEW_PATHS. Each side (this admin
// dashboard, and each of the 3 client repos) resolves the id to its OWN
// local logo path (used when logoSource is "library"). `logo` field uses
// the same type: "imagePicker" mechanism as hero's image field — real
// upload goes through siteConfigService.uploadImage (GCS), same as hero.
export const NAVBAR_LOGO_OPTIONS = [
    { value: "meelz-logo", label: "Logo 1" },
    { value: "e4f-logo",   label: "Logo 2" },
    { value: "ck-logo",    label: "Logo 3" },
];

// gwt-admin's OWN preview-only path lookup (this dashboard's public/navbar-logos/).
// Client repos do NOT use this map — each resolves the same ids to paths in
// ITS OWN /public folder (meelz-client-v2's /logo.svg, ck-client-e4f's
// /logo.jpeg, ck-client-ck's /logo-ck.png).
export const NAVBAR_LOGO_ADMIN_PREVIEW_PATHS = {
    "meelz-logo": "/navbar-logos/meelz-logo.svg",
    "e4f-logo":   "/navbar-logos/e4f-logo.jpeg",
    "ck-logo":    "/navbar-logos/ck-logo.png",
};

// Per-item fields for the `navLinks` cards field — {label, href}, using the
// existing type: "cards" mechanism (same as footer's earlier linkColumns
// approach). `label` is translatable copy; `href` is a URL, never
// translatable. `href`'s `type: "sectionAnchor"` renders as a dropdown of
// this layout's known hideable sections (Quiz/FAQ) + a "Custom URL" escape
// hatch, instead of a bare text box — see sectionAnchors.constants.js.
// SchemaFieldControl.js resolves the actual per-layout `options` at render
// time (the registry is layout-specific; this field definition itself
// isn't).
export const NAVBAR_LINK_FIELDS = [
    { field: "label", label: "Label", translatable: true, wrapControl: true },
    { field: "href",  label: "URL", type: "sectionAnchor" },
];
export const NAVBAR_LINK_NEW_ITEM = { label: { en: "New Link" }, href: "#" };

// Per-item fields for the `socialLinks` cards field — {platform, url}.
// `platform` is a fixed dropdown (see socialPlatforms.constants.js), not a
// generic icon picker: replaces the earlier hardcoded-emoji social icons
// (including a wrong airplane emoji mistakenly used for Telegram) with a
// real per-icon URL and a correct, unambiguous brand icon per platform.
export const NAVBAR_SOCIAL_LINK_FIELDS = [
    { field: "platform", label: "Platform", type: "select", options: SOCIAL_PLATFORM_OPTIONS },
    { field: "url",      label: "URL" },
];
export const NAVBAR_SOCIAL_LINK_NEW_ITEM = { platform: "facebook", url: "" };

// MEELZ real values: --color-bg (#F9F3E6) main bar, --color-secondary
// (#1E1E1E) link text, .main_btn (bg-black, rounded-full) CTA button,
// nav absolutely centered (linksAlignment "center"), logo left + actions
// right (barJustify "space-between"). A SEPARATE top promo bar
// (--color-bg-navbar #332F2E) sits above the main bar, real message
// "Order For 700AED Will Earn +7AED Bonuses" (t("topBarMessage")) on the
// left, real social icons (Facebook/Instagram/Telegram) on the right —
// confirmed live, previously missing from this schema entirely.
export const NAVBAR_LAYOUT_MEELZ_DEFAULT_PROPS = {
    logoSource:      "library",
    logo:            "meelz-logo",
    bgColor:         "#F9F3E6",
    textColor:       "#1E1E1E",
    iconColor:       "#1E1E1E",
    // "How It Works"/"Delivery" removed (2026-09-16) — the live client
    // dropped both anchors along with the static sections they pointed at
    // (HowItWorks/DeliveryBanner components; see meelz-client-v2's
    // "remove outdated sections and links from Home, Navbar, and
    // MainFooter" commit). Reset-to-defaults was still serving these two
    // dead links until this fix — confirmed via direct client report.
    navLinks: [
        { label: { en: "Find My Plan" }, href: "/#menu" },
        { label: { en: "Meal Plans" },   href: "/#perfect-plan" },
        { label: { en: "FAQ" },          href: "/#faq" },
    ],
    ctaText:         { en: "Log In" },
    ctaColor:        "#000000",
    ctaTextColor:    "#ffffff",
    ctaBorderRadius: "pill",
    logoAlignment:   "left",
    barJustify:      "space-between",
    linksAlignment:  "center",
    authOrder:       "auth-lang-logout",
    showSearchIcon:  false,
    showSocialIcons: true,
    socialLinks: [
        { platform: "facebook",  url: "https://www.facebook.com/profile.php?id=61578818357493" },
        { platform: "instagram", url: "https://www.instagram.com/meelz.ae?igsh=MWZjNXVpcXJyN2Y0&utm_source=qr" },
        { platform: "telegram",  url: "https://t.me/meelz_ae" },
    ],
    showTopBar:      true,
    topBarColor:     "#332F2E",
    topBarTextColor: "#ffffff",
    topBarMessage:   { en: "Order For 700AED Will Earn +7AED Bonuses" },
    topBarMessageAlignment: "right",
};

// E4F real values: bg-[#f7f7f7] pill navbar, --color-primary (#b11212) CTA
// button (rounded-full, white text), logo + social icons left, nav links +
// CTA left-clustered together (not centered, not spread) via a flex sibling
// group, matching hero's already-extracted E4F primary color.
export const NAVBAR_LAYOUT_E4F_DEFAULT_PROPS = {
    logoSource:      "library",
    logo:            "e4f-logo",
    bgColor:         "#f7f7f7",
    textColor:       "#000000",
    iconColor:       "#000000",
    // "Find My Plan"/"Meal Plans"/Delivery/FAQ point at E4F's real live
    // section ids (chef-kitchen.tsx's
    // wellness-quiz/diet-selection/delivery-zone/faq-section) — label text
    // renamed from the original Quiz/Menu wording to be more inviting/less
    // generic, real anchors unchanged. DeliveryZoneSection and
    // FAQSectionWrapper are both actually rendered (not commented out —
    // this entry previously claimed otherwise; only
    // TestimonialsSection/FreeDeliveryBanner remain commented out). Quiz and
    // FAQ each have their own Builder visibility toggle — the real
    // navbar-section.tsx auto-drops these two navLinks specifically when
    // their target section is hidden, so there's no dead-anchor risk from
    // publishing them here. No Contacts — E4F's real site has no contacts
    // section to link to.
    navLinks: [
        { label: { en: "Find My Plan" }, href: "/#wellness-quiz" },
        { label: { en: "Meal Plans" },   href: "/#diet-selection" },
        { label: { en: "Delivery" },     href: "/#delivery-zone" },
        { label: { en: "FAQ" },          href: "/#faq-section" },
    ],
    ctaText:         { en: "Login" },
    ctaColor:        "#b11212",
    ctaTextColor:    "#ffffff",
    ctaBorderRadius: "pill",
    logoAlignment:   "left",
    barJustify:      "space-between",
    linksAlignment:  "left",
    authOrder:       "auth-lang-logout",
    showSearchIcon:  false,
    showSocialIcons: true,
    socialLinks: [
        { platform: "instagram", url: "https://www.instagram.com/eatfor.fit_ge" },
        { platform: "facebook",  url: "https://www.facebook.com/eatforfitt" },
        { platform: "whatsapp",  url: "https://api.whatsapp.com/send?phone=995595800007&text=Gamarjoba%20Eat%20For%20Fit" },
        { platform: "telegram",  url: "https://t.me/eatfor_fit" },
    ],
    // E4F's real header has no separate top promo bar — confirmed, unlike
    // MEELZ's navbar.tsx.
    showTopBar:      false,
    topBarColor:     "#000000",
    topBarTextColor: "#ffffff",
    topBarMessage:   { en: "" },
    topBarMessageAlignment: "right",
};

// CK real values: bg-[#9D91B9] pill navbar, CTA bg-[#fbeb5a]/text-[#46298A]
// (rounded-full), same structural layout as E4F (logo+socials left, nav
// links left-clustered), matching hero's already-extracted CK colors.
export const NAVBAR_LAYOUT_CK_DEFAULT_PROPS = {
    logoSource:      "library",
    logo:            "ck-logo",
    bgColor:         "#9D91B9",
    textColor:       "#ffffff",
    iconColor:       "#ffffff",
    // "Find My Plan"/"Meal Plans"/Delivery/FAQ point at CK's real live
    // section ids (chef-kitchen.tsx's
    // wellness-quiz/diet-selection/delivery-zone/faq-section) — label text
    // renamed from the original Quiz/Menu wording to be more inviting/less
    // generic, real anchors unchanged. Plain anchor hrefs, not
    // scrollIntoView handler-matching, per plan.md Decision 5. Leading "/"
    // makes these ABSOLUTE-path anchors (/#diet-selection), not
    // relative-to-current-page ones — the navbar is now shared chrome
    // rendered on every route (terms/privacy/thank-you too, not just the
    // homepage), so a bare "#diet-selection" clicked from /terms would
    // resolve to /terms#diet-selection (wrong page, no such id there)
    // instead of navigating back to the homepage section. Quiz and FAQ each
    // have their own Builder visibility toggle — the real navbar-section.tsx
    // auto-drops these two navLinks specifically when their target section
    // is hidden, so there's no dead-anchor risk from publishing them here.
    // No Contacts link — it was a dead "#" anchor with no real section
    // behind it and has been removed from the real default-navbar.tsx
    // entirely.
    navLinks: [
        { label: { en: "Find My Plan" }, href: "/#wellness-quiz" },
        { label: { en: "Meal Plans" },   href: "/#diet-selection" },
        { label: { en: "Delivery" },     href: "/#delivery-zone" },
        { label: { en: "FAQ" },          href: "/#faq-section" },
    ],
    ctaText:         { en: "Login" },
    ctaColor:        "#fbeb5a",
    ctaTextColor:    "#46298A",
    ctaBorderRadius: "pill",
    logoAlignment:   "left",
    barJustify:      "space-between",
    linksAlignment:  "left",
    // CK's real default-navbar.tsx desktop nav order (LanguageDropdown ->
    // Login/Account button -> LogoutButton, lines 133-142) is
    // Language -> Login/Account -> Logout, not the Login/Account-first
    // option — confirmed bug, was previously "auth-lang-logout".
    authOrder:       "lang-auth-logout",
    showSearchIcon:  false,
    showSocialIcons: true,
    socialLinks: [
        { platform: "instagram", url: "https://www.instagram.com/chef_kitchen_ge/" },
        { platform: "facebook",  url: "https://www.facebook.com/chef.kitchen.ge" },
        { platform: "telegram",  url: "https://t.me/ChefKitchenGE" },
    ],
    // CK's real header has no separate top promo bar either — same as E4F.
    showTopBar:      false,
    topBarColor:     "#000000",
    topBarTextColor: "#ffffff",
    topBarMessage:   { en: "" },
    topBarMessageAlignment: "right",
};

export const NAVBAR_LAYOUTS = {
    meelz: { label: "Layout 1", defaultProps: NAVBAR_LAYOUT_MEELZ_DEFAULT_PROPS },
    e4f:   { label: "Layout 2", defaultProps: NAVBAR_LAYOUT_E4F_DEFAULT_PROPS },
    ck:    { label: "Layout 3", defaultProps: NAVBAR_LAYOUT_CK_DEFAULT_PROPS },
};

// Restore candidates for the "+ Restore" buttons (see CardsEditor.js) —
// reuses each layout's own defaultProps.navLinks directly rather than a
// separate hardcoded list, so the restorable set is always exactly that
// brand's real default links, however many there are (MEELZ has 5,
// CK/E4F have 4 — genuinely dynamic per brand, no duplicated data to keep
// in sync).
export const NAVBAR_DEFAULT_LINKS_BY_LAYOUT = {
    meelz: NAVBAR_LAYOUT_MEELZ_DEFAULT_PROPS.navLinks,
    e4f:   NAVBAR_LAYOUT_E4F_DEFAULT_PROPS.navLinks,
    ck:    NAVBAR_LAYOUT_CK_DEFAULT_PROPS.navLinks,
};

// Shared field shape — applies to every layout. Mobile hamburger-drawer
// internals stay dropped from extraction (StructuralNavbarPreview renders a
// simplified, visually accurate navbar, not a reproduction of full
// mobile-drawer logic) — but the auth cluster's ORDER (`authOrder`, below)
// is real, live-site-configurable: it was previously hardcoded with no
// admin control at all (confirmed bug — publishing a navbar change never
// moved Login/Account/Language/Logout). The nav link list itself (label +
// href per item) IS admin-editable via `navLinks`, using the same
// type: "cards" mechanism as footer's earlier linkColumns approach.
export const NAVBAR_SCHEMA = [
    { key: "logo",            label: "Logo",             type: "imagePicker", options: NAVBAR_LOGO_OPTIONS, previewPaths: NAVBAR_LOGO_ADMIN_PREVIEW_PATHS },
    { key: "bgColor",         label: "Background color", type: "color" },
    { key: "textColor",       label: "Text color",       type: "color" },
    { key: "iconColor",       label: "Icon color",        type: "color" },
    {
        key:            "navLinks",
        label:          "Nav Links",
        type:           "cards",
        fields:         NAVBAR_LINK_FIELDS,
        newItemDefault: NAVBAR_LINK_NEW_ITEM,
        // Nav links are a fixed, curated set matched against each brand's
        // real page sections (see sectionAnchors.constants.js) — an admin
        // can reorder/edit/remove the existing links or point one at a
        // custom URL, but can't add brand-new ones (there's no matching
        // page section for gwt-admin to link a freshly-added item to).
        allowAdd:       false,
        // If an admin accidentally removes one of the brand's default
        // links, a "+ Restore X" button lets them add that exact one back
        // — without allowAdd, deleting one would otherwise be unrecoverable
        // short of resetting the whole layout. See
        // NAVBAR_DEFAULT_LINKS_BY_LAYOUT above.
        restoreCandidatesByLayout: NAVBAR_DEFAULT_LINKS_BY_LAYOUT,
    },
    { key: "ctaText",         label: "Button text",       type: "text", translatable: true, wrapControl: true },
    { key: "ctaColor",        label: "Button color",      type: "color" },
    { key: "ctaTextColor",    label: "Button text color", type: "color" },
    { key: "ctaBorderRadius", label: "Button shape",      type: "select", options: NAVBAR_CTA_BORDER_RADIUS_OPTIONS },
    { key: "logoAlignment",   label: "Logo alignment",   type: "select", options: NAVBAR_LOGO_ALIGNMENT_OPTIONS },
    { key: "barJustify",      label: "Bar layout",       type: "select", options: NAVBAR_BAR_JUSTIFY_OPTIONS },
    { key: "linksAlignment",  label: "Links alignment",  type: "select", options: NAVBAR_LINKS_ALIGNMENT_OPTIONS },
    { key: "authOrder",       label: "Login/Language/Logout order", type: "select", options: NAVBAR_AUTH_ORDER_OPTIONS },
    { key: "showSearchIcon",  label: "Show search icon", type: "boolean" },
    { key: "showSocialIcons", label: "Show social icons", type: "boolean" },
    {
        key:            "socialLinks",
        label:          "Social Links",
        type:           "cards",
        fields:         NAVBAR_SOCIAL_LINK_FIELDS,
        newItemDefault: NAVBAR_SOCIAL_LINK_NEW_ITEM,
    },
    { key: "showTopBar",      label: "Show top bar",          type: "boolean" },
    { key: "topBarColor",     label: "Top bar color",         type: "color" },
    { key: "topBarTextColor", label: "Top bar text color",    type: "color" },
    { key: "topBarMessage",   label: "Top bar message",       type: "text", translatable: true, wrapControl: true },
    { key: "topBarMessageAlignment", label: "Top bar message alignment", type: "select", options: NAVBAR_TOP_BAR_MESSAGE_ALIGNMENT_OPTIONS },
];
